(() => {
  if (customElements.get('listing-delivery')) return;
  const DAY = 86400000;
  const dayOff = (day, schedule) => schedule === 'mon_fri' ? day === 0 || day === 6 : schedule === 'mon_sat' && day === 0;

  class ListingDelivery extends HTMLElement {
    // Calendar arithmetic uses UTC date-only values after reading the warehouse clock.
    // An explicit warehouse zone avoids visitor differences; local preserves the US legacy clock.
    static estimate(config, now = new Date()) {
      if (!Number.isInteger(config.min) || !Number.isInteger(config.max) || config.min < 0 || config.max > 60 || config.min > config.max) return null;
      const cutoff = /^(\d{2}):(\d{2})$/.exec(String(config.cutoff));
      if (!cutoff || Number(cutoff[1]) > 23 || Number(cutoff[2]) > 59) return null;
      if (!['mon_sat', 'mon_fri', 'every_day'].includes(config.dispatchDays) || !['calendar', 'business'].includes(config.transitDays)) return null;
      if (!['days', 'date'].includes(config.display) || !config.timeZone) return null;
      let parts;
      try {
        parts = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
          timeZone: config.timeZone === 'local' ? Intl.DateTimeFormat().resolvedOptions().timeZone : config.timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
        }).formatToParts(now).filter(part => part.type !== 'literal').map(part => [part.type, Number(part.value)]));
      } catch (_) {
        // Invalid editor time zone/date: retain the configured fallback, never invent a date.
        return null;
      }
      const today = Date.UTC(parts.year, parts.month - 1, parts.day);
      let dispatch = today + (parts.hour * 60 + parts.minute >= Number(cutoff[1]) * 60 + Number(cutoff[2]) ? DAY : 0);
      while (dayOff(new Date(dispatch).getUTCDay(), config.dispatchDays)) dispatch += DAY;
      const arrival = days => {
        let date = dispatch;
        for (let count = 0; count < days;) {
          date += DAY;
          if (config.transitDays === 'calendar' || !dayOff(new Date(date).getUTCDay(), 'mon_fri')) count++;
        }
        return date;
      };
      const earliest = arrival(config.min), latest = arrival(config.max);
      let text;
      if (config.display === 'date') {
        const options = { day: 'numeric', month: 'short', timeZone: 'UTC' };
        if (new Date(earliest).getUTCFullYear() !== parts.year || new Date(latest).getUTCFullYear() !== parts.year) options.year = 'numeric';
        const formatter = new Intl.DateTimeFormat('en-GB', options);
        const dates = earliest === latest ? formatter.format(earliest) : formatter.formatRange(earliest, latest);
        text = 'Estimated delivery: ' + dates;
      } else {
        const min = Math.round((earliest - today) / DAY), max = Math.round((latest - today) / DAY);
        text = max === 0 ? 'Expected to be delivered today' : `Expected to be delivered within ${min === max ? max : min + '–' + max} ${max === 1 ? 'day' : 'days'}`;
      }
      return { text, earliest, latest };
    }

    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected || this._connected) return;
        this._connected = true;
        this._text = this.querySelector('[data-delivery-text]');
        try { this._config = JSON.parse(this.dataset.deliveryConfig); }
        catch (_) { this.dataset.deliveryState = 'invalid-config'; return; }
        this._variantId = this._config.variantId;
        this._root = this.closest('[data-listing-product]');
        const input = this._root?.querySelector('[data-listing-purchase] [name="id"]');
        if (input) this._variantId = input.value;
        this._onVariant = event => {
          if (String(event.detail?.sectionId) !== this.dataset.sectionId) return;
          this._variantId = event.detail.variant?.id ?? null;
          this.update();
        };
        this._onChange = event => {
          if (event.target.name !== 'id' || !event.target.closest('[data-listing-purchase]')) return;
          this._variantId = event.target.value;
          this.update();
        };
        this._onVisibility = () => { if (!document.hidden) this.update(); };
        document.addEventListener('product:variant-change', this._onVariant);
        document.addEventListener('visibilitychange', this._onVisibility);
        this._root?.addEventListener('change', this._onChange);
        this.update();
        this._timer = window.setInterval(() => this.update(), 60000);
      });
    }

    disconnectedCallback() {
      this._connected = false;
      document.removeEventListener('product:variant-change', this._onVariant);
      document.removeEventListener('visibilitychange', this._onVisibility);
      this._root?.removeEventListener('change', this._onChange);
      window.clearInterval(this._timer);
    }

    update() {
      if (!this._text || !this._config) return;
      const config = this._config;
      const variant = config.variants.find(item => String(item.id) === String(this._variantId));
      let text = config.fallback, state = 'fallback';
      if (config.enabled && variant?.available) {
        if (config.message.trim()) { text = config.message; state = 'message'; }
        else if (!config.preorder && !variant.backorder) {
          const estimate = ListingDelivery.estimate(config);
          if (estimate) { text = estimate.text; state = 'estimate'; }
        }
      }
      if (this._text.textContent !== text) this._text.textContent = text;
      this.hidden = !text.trim();
      this.dataset.deliveryState = state;
    }
  }
  customElements.define('listing-delivery', ListingDelivery);
})();
